import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { apiMe, apiLogin, apiRegister, apiLogout, apiUpdateProfile } from "@/lib/api";

const AuthContext = createContext(null);

// user state machine:
//   null  → still checking session
//   false → unauthenticated
//   {...} → authenticated user
export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);

    const refresh = useCallback(async () => {
        try {
            const me = await apiMe();
            setUser(me);
            return me;
        } catch {
            // 401 on initial load is the "not logged in yet" signal — not an
            // error we want to log. We flip to `false` so ProtectedRoute can
            // redirect to /login.
            setUser(false);
            return null;
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const login = async (email, password) => {
        const data = await apiLogin({ email, password });
        setUser(data.user);
        return data.user;
    };

    const register = async (payload) => {
        const data = await apiRegister(payload);
        setUser(data.user);
        return data.user;
    };

    const logout = async () => {
        try {
            await apiLogout();
        } catch (err) {
            // Best-effort server-side logout — the local cookie may already
            // be expired, in which case the server returns 401 and that's
            // fine. Log so devs can still see real failures in the console.
            // eslint-disable-next-line no-console
            console.debug("apiLogout failed (continuing with local logout):", err);
        }
        setUser(false);
    };

    const updateProfile = async (payload) => {
        const updated = await apiUpdateProfile(payload);
        setUser(updated);
        return updated;
    };

    return (
        <AuthContext.Provider value={{ user, login, register, logout, refresh, updateProfile }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used within AuthProvider");
    return ctx;
}
